/**
 * App Sidebar - Main Navigation
 */

import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSession } from "@/state/session";
import { can } from "@/lib/rbac";
import {
  LayoutDashboard,
  Package,
  Boxes,
  PackageSearch,
  Factory,
  ShoppingCart,
  BarChart3,
  Settings,
  ChefHat,
  Warehouse,
  ClipboardList,
  ShoppingBag,
  Target,
  UserPlus,
  Users,
  BookOpen,
  Database,
  ChevronRight,
  CreditCard,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "./ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Skeleton } from "./ui/skeleton";

type NavItem = {
  title: string;
  url: string;
  icon: React.ElementType;
  resource:
    | "dashboard"
    | "products"
    | "ingredients"
    | "stock"
    | "batches"
    | "sales"
    | "reports"
    | "users"
    | "tipo_produtos"
    | "estabelecimentos"
    | "clientes"
    | "vendas"
    | "receitas"
    | "tabelas"
    | "billing";
  action: "view" | "create" | "manage";
};

const navGroups = [
  {
    label: "PRINCIPAL",
    items: [
      {
        title: "Dashboard",
        url: "/dashboard",
        icon: LayoutDashboard,
        resource: "dashboard" as const,
        action: "view" as const,
      },
    ],
  },
  {
    label: "INVENTÁRIO",
    items: [
      {
        title: "Tipos de Materiais",
        url: "/cadastro",
        icon: Package,
        resource: "products" as const,
        action: "view" as const,
      },
      {
        title: "Entrada no Estoque",
        url: "/entrada",
        icon: Boxes,
        resource: "ingredients" as const,
        action: "view" as const,
      },
      {
        title: "Controle de Estoque",
        url: "/estoque",
        icon: Warehouse,
        resource: "ingredients" as const,
        action: "view" as const,
      },
    ],
  },
  {
    label: "PRODUÇÃO",
    items: [
      {
        title: "Receitas",
        url: "/receitas",
        icon: BookOpen,
        resource: "receitas" as const,
        action: "view" as const,
      },
      {
        title: "Fornada",
        url: "/fornada",
        icon: Factory,
        resource: "batches" as const,
        action: "view" as const,
      },
    ],
  },
  {
    label: "SAÍDAS",
    items: [
      {
        title: "Produtos para Venda",
        url: "/produtos",
        icon: ClipboardList,
        resource: "tipo_produtos" as const,
        action: "view" as const,
      },
      {
        title: "Estabelecimentos",
        url: "/estabelecimentos",
        icon: ShoppingBag,
        resource: "estabelecimentos" as const,
        action: "view" as const,
      },
      {
        title: "Clientes",
        url: "/clientes",
        icon: Users,
        resource: "clientes" as const,
        action: "view" as const,
      },
      {
        title: "Vendas",
        url: "/vendas",
        icon: Target,
        resource: "vendas" as const,
        action: "create" as const,
      },
    ],
  },
  {
    label: "GERAL",
    items: [
      {
        title: "Relatórios",
        url: "/relatorios",
        icon: BarChart3,
        resource: "reports" as const,
        action: "view" as const,
      },
    ],
  },
  {
    label: "ADMINISTRADOR",
    items: [
      {
        title: "Usuários",
        url: "/users",
        icon: Users,
        resource: "users" as const,
        action: "manage" as const,
      },
      {
        title: "Tabelas",
        url: "/tabelas",
        icon: Database,
        resource: "tabelas" as const,
        action: "manage" as const,
      },
      {
        title: "Cobrança",
        url: "/billing",
        icon: CreditCard,
        resource: "billing" as const,
        action: "manage" as const,
      },
    ],
  },
];

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSession();
  const { open } = useSidebar();
  const [adminOpen, setAdminOpen] = React.useState(false);

  if (!user || !user.name || !user.email) {
    // ✅ Silencioso: skeleton handle o loading state
    return (
      <Sidebar>
        <SidebarContent>
          <div className="p-4 space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-3/4" />
            <Skeleton className="h-10 w-5/6" />
          </div>
        </SidebarContent>
      </Sidebar>
    );
  }

  const isActive = (url: string) => location.pathname.startsWith(url);

  const getInitials = (name: string) => {
    if (!name || name.trim().length === 0) return "U";

    const parts = name
      .trim()
      .split(/\s+/)
      .filter((part) => part.length > 0);

    if (parts.length === 0) return "U";
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();

    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const initials = getInitials(user.name);

  const roleLabels = {
    admin: "Administrador",
    gestor: "Gestor",
    funcionario: "Funcionário",
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <div className="flex items-center gap-2">
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <ChefHat className="size-4" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">Pudinzim Sorocaba</span>
                  <span className="truncate text-xs text-muted-foreground">Sistema de Gestão</span>
                </div>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {navGroups.map((group) => {
          // Skip ADMINISTRADOR group - it's rendered in the footer
          if (group.label === "ADMINISTRADOR") return null;

          const visibleItems = group.items.filter((item) => can(user.role, item.resource, item.action));

          if (visibleItems.length === 0) return null;

          // Normal groups
          return (
            <SidebarGroup key={group.label}>
              <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {visibleItems.map((item) => (
                    <SidebarMenuItem key={item.url}>
                      <SidebarMenuButton asChild isActive={isActive(item.url)} tooltip={item.title}>
                        <button onClick={() => navigate(item.url)}>
                          <item.icon />
                          <span>{item.title}</span>
                        </button>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          );
        })}
      </SidebarContent>

      <SidebarFooter>
        {/* Admin Section - Only visible to admins */}
        {(() => {
          const adminGroup = navGroups.find(g => g.label === "ADMINISTRADOR");
          if (!adminGroup) return null;

          const visibleItems = adminGroup.items.filter(item => can(user.role, item.resource, item.action));
          if (visibleItems.length === 0) return null;

          return (
            <SidebarMenu>
              <Collapsible open={adminOpen} onOpenChange={setAdminOpen} className="group/collapsible">
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip="Administrador">
                      <Settings className="h-4 w-4" />
                      <span>Administrador</span>
                      <ChevronRight className="ml-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {visibleItems.map((item) => (
                        <SidebarMenuSubItem key={item.url}>
                          <SidebarMenuSubButton asChild isActive={isActive(item.url)}>
                            <button onClick={() => navigate(item.url)}>
                              <item.icon className="h-4 w-4" />
                              <span>{item.title}</span>
                            </button>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            </SidebarMenu>
          );
        })()}

        {/* User Avatar */}
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <div className="flex items-center gap-2">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary text-primary-foreground">{initials}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{user.name}</span>
                  <span className="truncate text-xs text-muted-foreground">
                    {user.role ? roleLabels[user.role] : "Carregando..."}
                  </span>
                </div>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
